import { ChevronDown, Menu, X, ArrowUpRight, Circle } from "lucide-react";
import { useMemo, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import logo from "../../assets/skand-logo-long.png";
import {
  navServices,
  navSolutions,
  serviceGroups,
  services,
  solutionGroups,
  solutions,
} from "../../constants/site";

import { LinkButton } from "../ui/Button";
import { ThemeToggle } from "../theme/ThemeToggle";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Portfolio", href: "/portfolio" },
  { label: "About", href: "/about" },
  { label: "Blog", href: "/blog" },
  { label: "Contact", href: "/contact" },
];

export function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);

  const groupedServices = useMemo(
    () =>
      serviceGroups.map((group) => ({
        group,
        items: services.filter((service) => service.group === group),
      })),
    [],
  );
  const groupedSolutions = useMemo(
    () =>
      solutionGroups.map((group) => ({
        group,
        items: solutions.filter((solution) => solution.group === group),
      })),
    [],
  );

  // console.log("serviceGroups:", serviceGroups);
  // console.log("services:", services);
  // console.log(
  //   "service groups from services:",
  //   services.map((service) => service.group),
  // );

  return (
    <header className="sticky inset-x-0 top-0 z-50 border-b border-white/10 bg-ink-950/90 backdrop-blur-xl">
      <nav
        aria-label="Main navigation"
        className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8"
      >
        <Link
          to="/"
          className="flex items-center rounded focus:outline-none focus-visible:ring-2 focus-visible:ring-gold-300"
          aria-label="Skand Tech Hub home"
        >
          <img
            src={logo}
            alt="Skand Tech Hub"
            className="h-14 w-auto max-w-[200px] object-contain"
          />
        </Link>
        <div className="hidden items-center gap-1 lg:flex">
          <MegaMenu label="Services" groups={groupedServices} />
          <MegaMenu label="Solutions" groups={groupedSolutions} />
          {navLinks.slice(1).map((link) => (
            <NavLink
              key={link.href}
              to={link.href}
              className={({ isActive }) =>
                `rounded-md px-3 py-2 text-sm font-medium transition hover:bg-white/10 ${isActive ? "text-gold-500" : "text-ink"}`
              }
            >
              {link.label}
            </NavLink>
          ))}
        </div>
        <div className="hidden items-center gap-3 lg:flex">
          {/* <ThemeToggle /> */}
          <LinkButton to="/contact" className="min-h-11 px-4 py-2">
            Contact Us
          </LinkButton>
        </div>
        <button
          type="button"
          className="inline-flex h-11 w-11 items-center justify-center rounded-md border border-white/10 text-white lg:hidden"
          aria-label="Open navigation menu"
          aria-expanded={mobileOpen}
          onClick={() => setMobileOpen((open) => !open)}
        >
          {mobileOpen ? (
            <X className="h-5 w-5" />
          ) : (
            <Menu className="h-5 w-5" />
          )}
        </button>
      </nav>
      {mobileOpen ? (
        <div className="max-h-[calc(100vh-5rem)] overflow-y-auto border-t border-white/10 bg-ink-950 px-4 py-5 lg:hidden">
          <MobileGroup
            title="Services"
            groups={groupedServices}
            basePath="services"
            onNavigate={() => setMobileOpen(false)}
          />
          <MobileGroup
            title="Solutions"
            groups={groupedSolutions}
            basePath="solutions"
            onNavigate={() => setMobileOpen(false)}
          />
          <div className="mt-4 grid gap-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                onClick={() => setMobileOpen(false)}
                className="rounded-md px-3 py-3 font-medium text-white/80 hover:bg-white/10"
              >
                {link.label}
              </Link>
            ))}
          </div>
          {/* <ThemeToggle className="mt-5 w-full justify-center" /> */}
        </div>
      ) : null}
    </header>
  );
}

function MegaMenu({
  label,
  groups,
}: {
  label: string;
  groups: Array<{ group: string; items: typeof services }>;
}) {
  const [activeGroup, setActiveGroup] = useState(groups[0]?.group ?? "");

  const activeData =
    groups.find(({ group }) => group === activeGroup) ?? groups[0];

  return (
    <div className="group relative">
      {/* Trigger */}
      <button
        type="button"
        className="flex items-center gap-1 rounded-md px-3 py-2 text-sm font-medium text-white/75 transition hover:bg-white/10"
      >
        {label}

        <ChevronDown
          className="h-4 w-4 transition-transform duration-200 group-hover:rotate-180"
          aria-hidden="true"
        />
      </button>

      {/* Dropdown */}
      <div className="invisible absolute left-1/2 top-full w-[820px] -translate-x-1/2 pt-3 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
        <div className="overflow-hidden rounded-xl border border-white/10 bg-ink-950 shadow-2xl ring-1 ring-white/5">
          {/* Header */}
          <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.18em] text-gold-500">
                {label}
              </p>

              <p className="mt-1 text-xs text-white/40">
                Explore our digital capabilities
              </p>
            </div>

            <Link
              to={label === "Services" ? "/services" : "/solutions"}
              className="group/explore inline-flex items-center gap-1 text-xs font-semibold text-gold-500 transition hover:text-gold-200"
            >
              Explore All
              <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover/explore:-translate-y-0.5 group-hover/explore:translate-x-0.5" />
            </Link>
          </div>

          {/* Main Content */}
          <div className="grid grid-cols-[215px_1fr]">
            {/* Categories */}
            <div className="border-r border-white/10 bg-white/[0.02] p-3">
              <p className="mb-2 px-2 text-[9px] font-semibold uppercase tracking-[0.18em] text-white/25">
                Categories
              </p>

              <div className="space-y-0.5">
                {groups.map(({ group, items }) => {
                  const active = activeGroup === group;

                  return (
                    <button
                      key={group}
                      type="button"
                      onMouseEnter={() => setActiveGroup(group)}
                      onFocus={() => setActiveGroup(group)}
                      onClick={() => setActiveGroup(group)}
                      className={`group/category flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left transition-all ${
                        active ? "bg-gold-300/10" : "hover:bg-white/[0.04]"
                      }`}
                    >
                      <div className="min-w-0">
                        <p
                          className={`truncate text-xs font-semibold ${
                            active ? "text-gold-500" : "text-white/70"
                          }`}
                        >
                          {group}
                        </p>

                        <p className="mt-0.5 text-[10px] text-white/25">
                          {items.length} {items.length === 1 ? "item" : "items"}
                        </p>
                      </div>

                      <ArrowUpRight
                        className={`h-3.5 w-3.5 shrink-0 transition-all ${
                          active
                            ? "translate-x-0 opacity-100 text-gold-500"
                            : "-translate-x-1 opacity-0 group-hover/category:translate-x-0 group-hover/category:opacity-50"
                        }`}
                      />
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Services / Solutions */}
            <div className="min-w-0 p-5">
              {/* Heading */}
              <div className="mb-4 flex items-center justify-between">
                <div className="min-w-0">
                  <p className="text-[9px] font-semibold uppercase tracking-[0.18em] text-gold-500/60">
                    {label === "Services" ? "Services" : "Solutions"}
                  </p>

                  <h3 className="mt-1 truncate text-base font-semibold text-white">
                    {activeData?.group}
                  </h3>
                </div>

                <span className="ml-4 shrink-0 text-[10px] text-white/25">
                  {activeData?.items.length ?? 0}{" "}
                  {label === "Services" ? "services" : "solutions"}
                </span>
              </div>

              {/* Items */}
              <div className="grid grid-cols-2 gap-1.5">
                {activeData?.items.map((item) => {
                  const Icon = item.icon;

                  return (
                    <Link
                      key={item.slug}
                      to={`/${item.slug}`}
                      className="group/item flex min-w-0 items-center gap-2 rounded-lg px-2.5 py-2 transition hover:bg-white/[0.05]"
                    >
                      <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md bg-white/[0.04] text-primary transition group-hover/item:bg-gold-300/10 group-hover/item:text-gold-500">
                        <Icon className="h-3.5 w-3.5" />
                      </span>

                      <span className="min-w-0 flex-1 truncate text-xs font-medium text-white/60 transition group-hover/item:text-white">
                        {item.title}
                      </span>

                      <ArrowUpRight className="h-3 w-3 shrink-0 text-white/20 opacity-0 transition group-hover/item:translate-x-0.5 group-hover/item:-translate-y-0.5 group-hover/item:opacity-70" />
                    </Link>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Bottom Strip */}
          <div className="flex items-center justify-between border-t border-white/10 bg-white/[0.015] px-5 py-3">
            <p className="text-[11px] text-white/35">
              Have something specific in mind?
            </p>

            <Link
              to="/contact"
              className="inline-flex items-center gap-1 text-[11px] font-semibold text-gold-500 transition hover:text-gold-200"
            >
              Start a Project
              <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

function MobileGroup({
  title,
  groups,
  onNavigate,
}: {
  title: string;
  groups: Array<{ group: string; items: typeof services }>;
  basePath: "services" | "solutions";
  onNavigate: () => void;
}) {
  return (
    <details className="border-b border-white/10 py-3">
      <summary className="flex cursor-pointer list-none items-center justify-between font-semibold text-white [&::-webkit-details-marker]:hidden">
        <span>{title}</span>

        <ChevronDown className="h-4 w-4 text-white/50 transition-transform duration-200" />
      </summary>

      <div className="mt-4 space-y-2">
        {groups.map(({ group, items }) => (
          <details
            key={group}
            className="overflow-hidden rounded-xl border border-white/10 bg-white/[0.02]"
          >
            <summary className="flex cursor-pointer list-none items-center justify-between px-4 py-3 text-sm font-semibold text-white/80 [&::-webkit-details-marker]:hidden">
              <div>
                <p>{group}</p>

                <p className="mt-0.5 text-[11px] font-normal text-white/35">
                  {items.length} services
                </p>
              </div>

              <ChevronDown className="h-4 w-4 text-white/40" />
            </summary>

            <div className="border-t border-white/10 px-2 py-2">
              {items.map((item) => {
                const Icon = item.icon;

                return (
                  <Link
                    key={item.slug}
                    to={`/${item.slug}`}
                    onClick={onNavigate}
                    className="flex items-center gap-2 rounded-lg px-3 py-2.5 text-sm text-white/60 transition hover:bg-white/5 hover:text-white"
                  >
                    <Icon className="h-4 w-4 shrink-0 text-primary" />

                    <span>{item.title}</span>

                    <ArrowUpRight className="ml-auto h-3.5 w-3.5 text-white/20" />
                  </Link>
                );
              })}
            </div>
          </details>
        ))}

        <Link
          to="/services"
          onClick={onNavigate}
          className="flex items-center justify-between rounded-xl border border-gold-300/15 bg-gold-300/5 px-4 py-3 text-sm font-semibold text-gold-500 transition hover:bg-gold-300/10"
        >
          <span>Explore All</span>

          <ArrowUpRight className="h-4 w-4" />
        </Link>
      </div>
    </details>
  );
}
